import * as React from 'react';
import { useState, useEffect } from 'react';
import { ISchedule } from '../../../../../../common/constants';
import { Plus, Edit, Trash, Close } from '../../../../../../components/Icons';
import Card from './card';

interface IScheduleTimeProps {
  inputData: ISchedule[];
  onScheduleChange?: (schedules: ISchedule[]) => void;
}

const ScheduleTime: React.FunctionComponent<IScheduleTimeProps> = ({
  inputData,
  onScheduleChange
}) => {
  const [schedules, setSchedules] = useState<ISchedule[]>(inputData);
  const [editingIndex, setEditingIndex] = useState<number | null>(null);
  const [isAddingNew, setIsAddingNew] = useState<boolean>(false);
  const [newSchedule, setNewSchedule] = useState<ISchedule>({ time: '', value: 0 });

  // Update schedules when inputData changes
  useEffect(() => {
    setSchedules(inputData);
  }, [inputData]);

  const handleEdit = (index: number) => {
    setEditingIndex(index);
  };

  const handleDelete = (index: number) => {
    const updatedSchedules = schedules.filter((_, i) => i !== index);
    setSchedules(updatedSchedules);
    onScheduleChange?.(updatedSchedules);
  };

  const handleSaveEdit = (index: number) => {
    setEditingIndex(null);
    onScheduleChange?.(schedules);
  };

  const handleCancelEdit = () => {
    setEditingIndex(null);
    setSchedules(inputData); // Reset to original data
  };

  const handleTimeChange = (index: number, time: string) => {
    const updatedSchedules = [...schedules];
    updatedSchedules[index].time = time;
    setSchedules(updatedSchedules);
  };

  const handleValueChange = (index: number, value: number) => {
    const updatedSchedules = [...schedules];
    updatedSchedules[index].value = value;
    setSchedules(updatedSchedules);
  };

  const handleAddNew = () => {
    setIsAddingNew(true);
    setNewSchedule({ time: '', value: 0 });
  };

  const handleSaveNew = () => {
    if (newSchedule.time && newSchedule.value >= 0 && validateNewTime(newSchedule.time)) {
      const updatedSchedules = [...schedules, newSchedule];
      setSchedules(updatedSchedules);
      onScheduleChange?.(updatedSchedules);
      setIsAddingNew(false);
      setNewSchedule({ time: '', value: 0 });
    }
  };

  const handleCancelNew = () => {
    setIsAddingNew(false);
    setNewSchedule({ time: '', value: 0 });
  };

  const validateTimeFormat = (time: string): boolean => {
    const timeRegex = /^([0-1]?[0-9]|2[0-3]):[0-5][0-9]$/;
    return timeRegex.test(time);
  };

  const validateNewTime = (newTime: string): boolean => {
    if (!validateTimeFormat(newTime)) return false;
    
    // If no existing schedules, any valid time is acceptable
    if (schedules.length === 0) return true;
    
    // Get the last schedule time
    const lastSchedule = schedules[schedules.length - 1];
    if (!lastSchedule.time) return true;
    
    // Convert times to minutes for comparison
    const newTimeMinutes = convertTimeToMinutes(newTime);
    const lastTimeMinutes = convertTimeToMinutes(lastSchedule.time);
    
    return newTimeMinutes > lastTimeMinutes;
  };

  const convertTimeToMinutes = (time: string): number => {
    const [hours, minutes] = time.split(':').map(Number);
    return hours * 60 + minutes;
  };

  return (
    <div className="flex flex-1 px-4 rounded-xl mx-auto">

      {/* Schedule List */}
      <div className="w-full ">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold text-gray-900">Lịch trình</h3>
          <button
            className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-lg font-semibold transition-all duration-300 flex items-center gap-2 disabled:cursor-not-allowed"
            onClick={handleAddNew}
            disabled={isAddingNew}
          >
            <Plus />
            <span>Thêm mới</span>
          </button>
        </div>

        <div className="space-y-3">
          {schedules.map((schedule, index) => (
            <Card
              key={index}
              index={index}
              editingIndex={editingIndex}
              schedule={schedule}
              handleTimeChange={handleTimeChange}
              handleValueChange={handleValueChange}
              handleSaveEdit={handleSaveEdit}
              handleCancelEdit={handleCancelEdit}
              handleDelete={handleDelete}
              handleEdit={handleEdit}
              validateTimeFormat={validateTimeFormat}
            />
          ))}

          {/* Add new schedule form */}
          {isAddingNew && (
            <div className="bg-blue-50 border-2 border-dashed border-blue-500 rounded-lg">
              <div className="p-5">
                <div className="flex gap-3 mb-4">
                  <div className="flex-1">
                    <input
                      type="time"
                      step="60"
                      className={`w-full border rounded-md px-3 py-2 focus:outline-none focus:ring-2 ${
                        newSchedule.time && !validateNewTime(newSchedule.time)
                          ? 'border-red-500 focus:border-red-500 focus:ring-red-200'
                          : 'border-gray-300 focus:border-blue-500 focus:ring-blue-200'
                      }`}
                      value={newSchedule.time}
                      onChange={(e) => setNewSchedule({ ...newSchedule, time: e.target.value })}
                      placeholder="Thời gian"
                      style={{ 
                        '--webkit-calendar-picker-indicator-color': '#3b82f6',
                        '--webkit-calendar-picker-indicator-hover-color': '#2563eb'
                      } as React.CSSProperties}
                    />
                  </div>
                  <input
                    type="number"
                    className="flex-1 border border-gray-300 rounded-md px-3 py-2 focus:border-blue-500 focus:outline-none focus:ring-2 focus:ring-blue-200"
                    min="0"
                    max="100"
                    value={newSchedule.value}
                    onChange={(e) => setNewSchedule({ ...newSchedule, value: parseInt(e.target.value) || 0 })}
                    placeholder="Giá trị (%)"
                  />
                </div>
                {newSchedule.time && !validateNewTime(newSchedule.time) && (
                      <p className="text-red-500 text-sm mt-1">
                        Thời gian phải sau thời gian cuối cùng ({schedules.length > 0 ? schedules[schedules.length - 1].time : ''})
                      </p>
                    )}
                <div className="flex gap-3 justify-end">
                  <button
                    className="bg-blue-600 hover:bg-blue-700 disabled:bg-gray-400 text-white px-4 py-2 rounded-md font-semibold transition-all duration-300 disabled:cursor-not-allowed"
                    onClick={handleSaveNew}
                    disabled={!validateTimeFormat(newSchedule.time) || !validateNewTime(newSchedule.time)}
                  >
                    <span>Thêm</span>
                  </button>
                  <button
                    className="border border-gray-300 hover:bg-gray-100 text-gray-700 px-4 py-2 rounded-md font-medium transition-all duration-300 flex items-center gap-2"
                    onClick={handleCancelNew}
                  >
                    <Close />
                    <span>Hủy</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {schedules.length === 0 && !isAddingNew && (
            <div className="text-center py-10 text-gray-500 italic bg-gray-50 rounded-lg border border-dashed border-gray-300">
              <p className="text-base">Chưa có lịch trình nào. Hãy thêm lịch trình mới!</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ScheduleTime;
